import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import ButtonText from "components/common/buttonText";
import InputDate from "components/common/inputDate";
import InputDateAndTime from "components/common/inputDateAndTime/inputDateAndTime";
import ToggleText from "components/common/toggleText";
import WidgetLoadingSkeleton from "components/dashboard/widgetLoadingSkeleton";
import DropdownSelector from "components/common/dropdownSelector/dropdownSelector";
import InputNumber from "components/common/inputNumber";
import InputText from "components/common/inputText/inputText";
import InputTime from "components/common/inputTime";
import SelectParkingSpotsModal from "components/dashboard/selectParkingSpotsModal";
import ButtonSlider from "components/common/buttonSlider";
import CollapsibleContainer from "components/common/collapsibleContainer";
import InputPhoneNumber from "components/common/inputPhoneNumber";
import Alert from "components/common/alert";
import CommunitiesModal from "pages/communities/communitiesModal";

import { getParkingSpots } from "api/locations";
import { 
  createMarketplacePermit, 
  createPermit, 
  getPermit, 
  updatePermit,
  getParkingSpotsWithNumberOfPermitsForParkingArea
} from "api/permits";

import { getToken } from "context/auth";
import { useDashboard } from "context/dashboard";
import { useMessages } from "context/messages";

import { format } from "date-fns";
import BackButton from "components/dashboard/backButton";
import { getCommunitiesForParkingArea, removeUserFromCommunity } from "api/communities";
import { getPermitQueueItem } from "api/permitQueue";
import { getContractCodes } from "api/contractCodes";
import { PermitQueueType } from "../../helpers/consts";
import countries from "../../helpers/countries";

import { useOrganizationFeatures } from "hooks/useOrganizationFeatures";

const PermitPage = ({ isMarketplace }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const { parkingAreaExtId, permitExtId, permitQueueItemExtId } = useParams();

    const { organization } = useDashboard();
    const { showToast, showBetweenPagesMessage } = useMessages();

    const [permit, setPermit] = useState({});
    const [isLoading, setIsLoading] = useState();
    const [isSaving, setIsSaving] = useState();
    const [parkingSpots, setParkingSpots] = useState([]);
    const [parkingSpotPrices, setParkingSpotPrices] = useState({});
    const [displayParkingSpotsModal, setDisplayParkingSpotsModal] = useState();
    const [communities, setCommunities] = useState();
    const [displayCommunitiesModal, setDisplayCommunitiesModal] = useState(false);
    const [selectedCommunityExtIds, setSelectedCommunityExtIds] = useState([]);
    const [permitQueueItem, setPermitQueueItem] = useState();
    const [isSpecificParkingSpots, setIsSpecificParkingSpots] = useState();
    const [isCommunitySyncing, setIsCommunitySyncing] = useState(false);
    const [contractCodes, setContractCodes] = useState([]);
    const [selectedContractCodeId, setSelectedContractCodeId] = useState("");
    const [validationMessages, setValidationMessages] = useState({
        phone: '',
        description: '',
        price: '',
    });

    const permitQueueItemCountry = useMemo(() => {
        return countries.find((c) => permitQueueItem?.phone?.startsWith(c.code)) ?? null;
    }, [permitQueueItem]);

    const { hasFeature } = useOrganizationFeatures();
    const isSublettingFeatureDisabled = hasFeature('PERMIT_SUBLETTING_DISABLED')
    const spots = permit.parkingSpots ?? [];

    const numberedSpots = spots
    .filter(s => s.isNumbered && s.spotIdentifier)
    .map(s => s.spotIdentifier)
    .sort((a, b) => a.localeCompare(b));

    const hasUnnumberedSpots = spots.some(s => !s.isNumbered);

    const description = [
    ...numberedSpots,
    hasUnnumberedSpots ? t('createPermitPage.notNumbered') : null
    ].filter(Boolean).join(', ');

    const fetchParkingSpotPrices = async () => {
        try {
            const token = await getToken();
            const result = await getParkingSpotsWithNumberOfPermitsForParkingArea(token, parkingAreaExtId);
            
            if (result && !result.error) {
                const prices = {};
                result.forEach(spot => {
                    if (spot.price != null) {
                        prices[spot.parkingSpotExtId] = spot.price;
                    }
                });
                setParkingSpotPrices(prices);
            }
        } catch (error) {
            console.error("Error fetching parking spot prices:", error);
        }
    };

    useEffect(() => {
        const loadPermit = async () => {
            setIsLoading(true);

            try {
                const token = await getToken();

                // Fetch parking spots and their prices in parallel
                const [parkingSpotsResult] = await Promise.all([
                    getParkingSpots(token, parkingAreaExtId),
                    fetchParkingSpotPrices()
                ]);

                if (parkingSpotsResult && !parkingSpotsResult.error) {
                    setParkingSpots(parkingSpotsResult.filter((parkingSpot) => { return parkingSpotsResult }));
                } else {
                    console.error(parkingSpotsResult);
                    showToast(t('generic.errorTitle'), t('generic.errorDescription'), 'error');
                }

                const communitiesResult = await getCommunitiesForParkingArea(token, parkingAreaExtId);

                if (communitiesResult && !communitiesResult.error) {
                    setCommunities(communitiesResult);
                } else {
                    console.error(communitiesResult);
                    showToast(t('generic.errorTitle'), t('generic.errorDescription'), 'error');
                }

                // Load contract codes for the parking area
                try {
                    const codesResult = await getContractCodes(token, parkingAreaExtId, false);
                    if (codesResult && !codesResult.error && codesResult.codes) {
                        setContractCodes(codesResult.codes);
                    }
                } catch (e) {
                    // Contract codes may not be available for all parking areas
                    console.log('No contract codes available:', e);
                }

                let permitQueueItem;

                if (permitQueueItemExtId) {
                    permitQueueItem = await getPermitQueueItem(token, permitQueueItemExtId);

                    if (permitQueueItem && !permitQueueItem.error) {
                        setPermitQueueItem(permitQueueItem);
                    } else {
                        console.error(permitQueueItem);
                        showToast(t('generic.errorTitle'), t('generic.errorDescription'), 'error');
                    }
                }

                /* If we don't have a permitExtId then we are adding a new permit, default to all parking spots */
                if (!permitExtId) {
                    const description = (permitQueueItem?.firstName || permitQueueItem?.lastName) ? permitQueueItem.firstName + ' ' + permitQueueItem.lastName : '';

                    const isSpecificSpotsPermit = permitQueueItem?.permitQueueType === PermitQueueType.AnyNumberedSpot;

                    setPermit({
                        phone: null,
                        phoneCountryNumberPrefix: '',
                        isSpecificParkingSpots: isSpecificSpotsPermit,
                        parkingSpots: [],
                        description,
                        isRecurring: false,
                        parkingAreaExtId: parkingAreaExtId,
                        price: null,
                        unit: 'month',
                        startDateTime: new Date(),
                        endDateTime: null,
                        monday: true,
                        tuesday: true,
                        wednesday: true,
                        thursday: true,
                        friday: true,
                        saturday: true,
                        sunday: true,
                        timeFrom: '00:00:00',
                        timeTo: '23:59:00',
                        isSublettingAllowed: isSublettingFeatureDisabled ? false : true,
                        isInvoicedByAirPark: true,
                        noEndDate: true,
                        isCommunity: false,
                        isMarketplace: isMarketplace ?? false,
                        addUserToCommunities: false,
                        reAddToMarketplace: true,
                        isManualActivation: false,
                        requiresContractCode: false,
                        manualActivatedEmail : permitQueueItem?.manualActivatedEmail,
                        manualActivatedFirstName : permitQueueItem?.manualActivatedFirstName,
                        manualActivatedLastName : permitQueueItem?.manualActivatedLastName,
                        manualActivatedLicencePlate : permitQueueItem?.manualActivatedLicencePlate,
                    });
                } else {
                    const result = await getPermit(token, permitExtId);
                    console.log('DEBUG getPermit response - requiresContractCode:', result?.requiresContractCode);

                    if (result && !result.error) {
                        setPermit({
                            ...result,
                            startDateTime: new Date(result.startDateTime),
                            endDateTime: result.endDateTime ? new Date(result.endDateTime) : null,
                            noEndDate: result.endDateTime == null,
                            parkingSpots: result.parkingSpots ?? [],
                            parkingSpotExtIds: result.parkingSpots?.map((parkingSpot) => { return parkingSpot.parkingSpotExtId }),
                            isInvoicedByAirPark: !result.isPaymentHandledManually,
                            isSpecificParkingSpots: result.parkSpotExtIds?.length > 0,
                            isMarketplace: isMarketplace,
                            addUserToCommunities: result.includeUserInParkingAreaCommunities,
                            isManualActivation: result.isManualActivation
                        });

                    } else {
                        console.error(result);
                        showToast(t('generic.errorTitle'), t('generic.errorDescription'), 'error');
                    }
                }
            } catch (error) {
                console.error(error);
                showToast(t('generic.errorTitle'), t('generic.errorDescription'), 'error');
            }

            setIsLoading(false);
        }

        loadPermit();
    }, [parkingAreaExtId, permitExtId]);

    useEffect(() => {
        if (permit.parkingSpots && permit.parkingSpots.length > 0) {

            const isSpecificParkingSpots = permit.parkingSpots.length > 0;
            setIsSpecificParkingSpots(isSpecificParkingSpots);

            const parkingSpotExtIds = permit
                .parkingSpots
                .map((parkingSpot) => { return parkingSpot.parkingSpotExtId })

            setPermit(
                {
                    ...permit,
                    isSpecificParkingSpots,
                    parkingSpotExtIds: parkingSpotExtIds
                });
        }
    }, [permit.parkingSpots])

    // 2026-05-25: Auto-defaulta unit='month' när pris > 0 sätts på AirPark-permit men
    // användaren inte uttryckligen klickat dropdown. DropdownSelector visar "kr/mån"
    // som default-text men onChange triggas inte vid mount → state.unit förblir null
    // för gamla permits (där DB har Unit=NULL). Utan default failade save med "Fält saknas".
    useEffect(() => {
        const hasPrice = permit.price !== null && permit.price !== undefined
            && permit.price !== '' && Number(permit.price) > 0;
        if (permit.isInvoicedByAirPark && hasPrice && !permit.unit) {
            setPermit(prev => ({ ...prev, unit: 'month' }));
        }
    }, [permit.price, permit.isInvoicedByAirPark, permit.unit])

    const handleToggleSpecificSpots = () => {
        if (permit.isSpecificParkingSpots) {
            setPermit({ ...permit, isSpecificParkingSpots: !permit.isSpecificParkingSpots, parkingSpots: [], parkingSpotExtIds: null })
        } else {
            setPermit({ ...permit, isSpecificParkingSpots: !permit.isSpecificParkingSpots })
        }
        setIsSpecificParkingSpots(false)
    }

    const handleToggleSpecificDates = () => {
        setPermit({ ...permit, isRecurring: !permit.isRecurring, monday: true, tuesday: true, wednesday: true, thursday: true, friday: true, saturday: true, sunday: true, timeFrom: '00:00:00', timeTo: '23:59:00' })
    }

    const handleToggleNoEndDate = () => {
        if (permit.noEndDate) {
            setPermit({ ...permit, noEndDate: false, endDateTime: new Date() })
        } else {
            setPermit({ ...permit, noEndDate: true, endDateTime: null })
        }
    };

    const handleToggleManualActivation = () => {
      const newisManualActivation = !permit.isManualActivation;
        
      setPermit({
        ...permit,
        isSublettingAllowed: newisManualActivation ? false : permit.isSublettingAllowed,
        isManualActivation: newisManualActivation,
        isInvoicedByAirPark: !newisManualActivation,
      });
    };

    const validatePhoneNumber = (isMarketplace, phone) => {
        if (permitExtId) return "";
        if (!phone) return "";
        const isPhoneEmpty = phone === '';
        const isPhoneInvalid = phone !== null && !/^\d{5,15}$/.test(phone);
        if (!isMarketplace && (isPhoneEmpty || isPhoneInvalid)) {
            return t('validation.invalidPhoneNumber');
        }
        return '';
    };

    const validateDescription = (description) => {
        if (!description) {
            return t('validation.requiredField');
        }
        return '';
    };

    const validatePrice = (price, isInvoicedByAirPark) => {
        // Allow price = 0 (for contract code permits), but reject null/undefined/NaN
        if ((price === null || price === undefined || price === '' || isNaN(price)) && isInvoicedByAirPark) {
            return t('validation.invalidPrice');
        }
        return '';
    };

    const handlePhoneChange = (phoneNumber, countryCode) => {
        setPermit({
            ...permit,
            phone: phoneNumber,
            phoneCountryNumberPrefix: countryCode
        });
        setValidationMessages({
            ...validationMessages,
            phone: validatePhoneNumber(phoneNumber)
        });
    };

    const handleDescriptionChange = (value) => {
        setPermit({ ...permit, description: value });
        setValidationMessages({
            ...validationMessages,
            description: validateDescription(value)
        });
    };

    const handlePriceChange = (value) => {
        setPermit({ ...permit, price: permit.isInvoicedByAirPark ? value : null});
        setValidationMessages({
            ...validationMessages,
            price: validatePrice(value, permit.isInvoicedByAirPark)
        });
    };

    const handleParkingSpotsChange = (selectedSpots) => {
        // If only one spot is selected and it has a price, update the permit price
        if (selectedSpots.length === 1) {
            const spotId = selectedSpots[0].parkingSpotExtId;
            if (parkingSpotPrices[spotId] && !permit.price) {
                setPermit(prev => ({
                    ...prev,
                    parkingSpots: selectedSpots,
                    price: permit.isInvoicedByAirPark ? parkingSpotPrices[spotId] : null
                }));
                return;
            }
        }
        
        // Otherwise, just update the selected spots
        setPermit(prev => ({
            ...prev,
            parkingSpots: selectedSpots
        }));
    };

    const hasFieldErrors = (isMarketplace, phone, description, price) => {
        const messages = {};
        messages.phone = validatePhoneNumber(isMarketplace, phone);
        messages.description = validateDescription(description);
        messages.price = validatePrice(price);
        setValidationMessages(messages);
        return messages.phone || messages.description || messages.price;
    };


    const handleSave = () => {
        if (isSaving) {
            return;
        }

        if (hasFieldErrors(isMarketplace, permit.phone ?? '', permit.description ?? '', permit.price ?? '')) {
            return;
        }

        if (permit.addUserToCommunities && (!(selectedCommunityExtIds && selectedCommunityExtIds.length > 0))) {
            showToast(t('generic.missingFieldsTitle'), 'Please select at least one community', 'error');
            return;
        }

        if (!isMarketplace && !permitExtId && !permitQueueItemExtId && ((!permit.phone || !permit.phoneCountryNumberPrefix) && !permit.isManualActivation)) {
            showToast(t('generic.missingFieldsTitle'), t('generic.missingFieldsDescription'), 'error');
            return;
        }

        // 2026-05-25: Tidigare krävdes både unit och price så fort AirPark fakturerar,
        // vilket gjorde att 0kr/null-permits (gratis-tilldelningar, marketplace utan pris)
        // inte gick att spara. Nu krävs unit BARA om pris > 0 — gratis-permits sparas
        // utan enhet. Påverkar ~30% av prod-permits som har Unit=NULL.
        const priceIsPositive = permit.price !== null && permit.price !== undefined
            && permit.price !== '' && Number(permit.price) > 0;
        if (permit.isInvoicedByAirPark && priceIsPositive && !permit.unit) {
            showToast(t('generic.missingFieldsTitle'), t('generic.missingFieldsDescription'), 'error');
            return;
        }

        const save = async () => {
            setIsSaving(true);

            try {
                const token = await getToken();

                if (!permitExtId) {
                    let result;
                    const createPermitRequestDto = {
                        description: permit.description,
                        parkingAreaExtId: parkingAreaExtId,
                        parkingSpotExtIds: permit.parkingSpotExtIds,
                        isSublettingAllowed: permit.isSublettingAllowed,
                        isPaymentHandledManually: !permit.isInvoicedByAirPark,
                        monday: permit.monday,
                        tuesday: permit.tuesday,
                        wednesday: permit.wednesday,
                        thursday: permit.thursday,
                        friday: permit.friday,
                        saturday: permit.saturday,
                        sunday: permit.sunday,
                        timeFrom: permit.timeFrom,
                        timeTo: permit.timeTo,
                        startDateTime: `${format(permit.startDateTime, 'yyyy-MM-dd')}T${format(permit.startDateTime, 'HH:mm')}`,
                        endDateTime: permit.endDateTime ? `${format(permit.endDateTime, 'yyyy-MM-dd')}T${format(permit.endDateTime, 'HH:mm')}` : null,
                        price: permit.price,
                        unit: permit.unit,
                        phone: permit.phone,
                        phoneCountryNumberPrefix: permit.phoneCountryNumberPrefix,
                        isRecurring: permit.isRecurring,
                        isCommunity: permit.isCommunity,
                        isMarketplace: isMarketplace,
                        includeUserInParkingAreaCommunities: permit.addUserToCommunities,
                        communityExtIds: permit.addUserToCommunities ? (selectedCommunityExtIds || []) : null,
                        permitQueueItemExtId: permitQueueItemExtId,
                        reAddToMarketplace: permit.reAddToMarketplace,
                        isManualActivation: permit.isManualActivation,
                        requiresContractCode: permit.requiresContractCode ?? false,
                        ...(selectedContractCodeId && { contractCodeId: selectedContractCodeId }),
                    };

                    if (isMarketplace) {
                        result = await createMarketplacePermit(token, createPermitRequestDto);
                    } else {
                        result = await createPermit(token, createPermitRequestDto);
                    }

                    if (result && !result.error) {
                        if (isMarketplace) {
                            navigate(`/${organization.organizationExtId.toLowerCase()}/parking-areas/${parkingAreaExtId}/created-marketplace-permit/${result.permitExtId}`);
                        } else {
                            showBetweenPagesMessage(
                                t('createPermitPage.permitCreatedTitle'),
                                permit.isManualActivation 
                                    ? t('createPermitPage.permitCreatedManualActivationDescription')
                                    : t('createPermitPage.permitCreatedDescription', { phone: permit.phone }),
                                () => { navigate(`/${organization.organizationExtId.toLowerCase()}/parking-areas/${parkingAreaExtId}/permits`) },
                                'success');
                        }
                    } else {
                        console.error(result);
                        showToast(t('generic.errorTitle'), t('generic.errorDescription'), 'error');
                    }
                } else {
                    console.log('DEBUG updatePermit - permit.requiresContractCode:', permit.requiresContractCode);
                    const result = await updatePermit(token, {
                        permitExtId: permit.permitExtId,
                        description: permit.description,
                        parkingAreaExtId: parkingAreaExtId,
                        parkingSpotExtIds: permit.parkingSpotExtIds,
                        isSublettingAllowed: permit.isSublettingAllowed,
                        isPaymentHandledManually: !permit.isInvoicedByAirPark,
                        monday: permit.monday,
                        tuesday: permit.tuesday,
                        wednesday: permit.wednesday,
                        thursday: permit.thursday,
                        friday: permit.friday,
                        saturday: permit.saturday,
                        sunday: permit.sunday,
                        timeFrom: permit.timeFrom,
                        timeTo: permit.timeTo,
                        startDateTime: `${format(permit.startDateTime, 'yyyy-MM-dd')}T${format(permit.startDateTime, 'HH:mm')}`,
                        endDateTime: permit.endDateTime ? `${format(permit.endDateTime, 'yyyy-MM-dd')}T${format(permit.endDateTime, 'HH:mm')}` : null,
                        price: permit.price,
                        unit: permit.unit,
                        isRecurring: permit.isRecurring,
                        isMarketplace: permit.isMarketplace,
                        isCommunity: permit.isCommunity,
                        includeUserInParkingAreaCommunities: permit.addUserToCommunities,
                        communityExtIds: permit.addUserToCommunities ? (selectedCommunityExtIds || []) : null,
                        reAddToMarketplace: permit.reAddToMarketplace,
                        requiresContractCode: permit.requiresContractCode ?? false,
                        ...(selectedContractCodeId && { contractCodeId: selectedContractCodeId }),
                        isManualActivation: permit.isManualActivation,
                        manualActivatedEmail: permit.manualActivatedEmail,
                        manualActivatedFirstName: permit.manualActivatedFirstName,
                        manualActivatedLastName: permit.manualActivatedLastName,
                        manualActivatedLicencePlate: permit.manualActivatedLicencePlate,
                    });

                    if (result && !result.error) {

                        // Can originate from marketplace but not currently be on marketplace (ie. assigned to user)
                        const assignedToUser = !!permit.userExtId;
                        const shouldNavigateToMarketplacePermitsPage = isMarketplace && !assignedToUser;

                        if (shouldNavigateToMarketplacePermitsPage) {
                            navigate(`/${organization.organizationExtId.toLowerCase()}/parking-areas/${parkingAreaExtId}/updated-marketplace-permit/${result.permitExtId}`);
                        } else {
                            showBetweenPagesMessage(
                                t('createPermitPage.permitUpdatedTitle'),
                                t('createPermitPage.permitUpdatedDescription'),
                                () => { navigate(`/${organization.organizationExtId.toLowerCase()}/parking-areas/${parkingAreaExtId}/permits${shouldNavigateToMarketplacePermitsPage ? '/marketplace' : ''}`) },
                                'success');
                        }
                    } else {
                        console.error(result);
                        showToast(t('generic.errorTitle'), t('generic.errorDescription'), 'error');
                    }
                }
            } catch (error) {
                console.error(error);
                showToast(t('generic.errorTitle'), t('generic.errorDescription'), 'error');
            }
            setIsSaving(false);
        }

        save();
    }

    return (
        <>
            <BackButton />
            <div className="flex flex-col m-auto max-w-[448px] w-full">
                {!isLoading && permit && organization && parkingSpots &&
                    <>
                        <div className="flex flex-col">
                            <h1 className="mt-14 text-center text-2xl font-medium mb-8">{permitExtId ? t('createPermitPage.editTitle') : isMarketplace ? t('createPermitPage.createTitle') : t('createPermitPage.issueTitle')}</h1>
                            {!isMarketplace && <div className={`flex flex-col mb-3 ${permitExtId ? 'opacity-50' : ''}`}>
                                <ButtonSlider
                                    title={t('createPermitPage.manualActivation')}
                                    onClick={permitExtId ? undefined : handleToggleManualActivation}
                                    isToggled={permit.isManualActivation}
                                    tooltip={permitExtId ? null : t('createPermitPage.manualActivationTooltip')} />
                            </div> }
                            {permitExtId && permit.isManualActivation && (
                                <div className="flex flex-col gap-2">
                                    <InputText
                                        placeHolder={t("permitTable.email")}
                                        title={t("permitTable.email")}
                                        value={permit.manualActivatedEmail}
                                        onChange={(value) => { setPermit({ ...permit, manualActivatedEmail: value }) }}
                                        error={permit.manualActivatedEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(permit.manualActivatedEmail) ? 'Please enter a valid email address' : ''}
                                        />
                                    
                                        <InputText
                                        placeHolder={t('permitTable.firstName')}
                                        title={t('permitTable.firstName')}
                                        value={permit.manualActivatedFirstName}
                                        onChange={(value) => { setPermit({ ...permit, manualActivatedFirstName: value }) }}
                                        />
                                    
                                    <InputText
                                        placeHolder={t('permitTable.lastName')}
                                        title={t('permitTable.lastName')}
                                        value={permit.manualActivatedLastName}
                                        onChange={(value) => { setPermit({ ...permit, manualActivatedLastName: value }) }}
                                        />
                                    
                                    <InputText
                                        placeHolder={t('permitTable.licensePlateNumber')}
                                        title={t('permitTable.licensePlateNumber')}
                                        value={permit.manualActivatedLicencePlate}
                                        onChange={(value) => { setPermit({ ...permit, manualActivatedLicencePlate: value }) }}
                                    />
                                </div>
                            )}

                            {!isMarketplace && <hr className="w-full mt-6 mb-6 border-airpark-gray-400" />}

                            {!permitExtId && !isMarketplace &&
                                (() => {
                                    const countryCode = permitQueueItemCountry ? permitQueueItemCountry.country : organization.countryCode;
                                    const phone = permitQueueItem?.phone ? permitQueueItem?.phone.replace(permitQueueItemCountry?.code, '') : permit.phone;
                                    return (
                                        <div className="flex flex-col mb-3 gap-3">
                                            <InputPhoneNumber
                                                title={t('createPermitPage.phoneNumber')}
                                                defaultCountryCode={countryCode}
                                                defaultNumber={phone}
                                                disabled={permitQueueItem?.phone || permit.isManualActivation}
                                                phoneNumber={phone}
                                                setPhoneNumber={handlePhoneChange}
                                                tooltip={t('createPermitPage.phoneNumberTooltip')}
                                                validationMessage={validationMessages.phone} />
                                        </div>
                                    );
                                })()
                            }

                            {permitQueueItem?.description &&
                                <div className="flex flex-col mb-3 gap-3">
                                    <InputText value={permitQueueItem.description} disabled={true} title={t('createPermitPage.specialRequests')} />
                                </div>
                            }

                            <div className="mb-3">
                                <InputText
                                    placeHolder='Jane Doe, Acme Company'
                                    title={t('createPermitPage.description')}
                                    value={permit.description} tooltip={t('createPermitPage.descriptionTooltip')}
                                    onChange={handleDescriptionChange} />
                                {validationMessages.description && <div className="text-red-500 text-sm mt-1">{validationMessages.description}</div>}
                            </div>
                            
                            <div className="flex flex-col mb-3 gap-3">
                                <ToggleText
                                    onClick={() => { handleToggleSpecificSpots(); }}
                                    title={t('createPermitPage.chooseParkingSpot')}
                                    defaultText={t('createPermitPage.allParkingSpots')}
                                    toggledText={t('createPermitPage.specificParkingSpots')}
                                    isToggled={permit.isSpecificParkingSpots} />

                                <CollapsibleContainer isExpanded={permit.isSpecificParkingSpots}>
                                    <div className="flex flex-col gap-y-3">
                                        {isSpecificParkingSpots &&
                                            <Alert
                                                template='info'
                                                title={t('createPermitPage.parkingSpots')}
                                                description={description}
                                            />
                                        }
                                        <ButtonText className="w-full mb-3" onClick={() => { setDisplayParkingSpotsModal(true); }} isSecondary={isSpecificParkingSpots}>
                                            {isSpecificParkingSpots ? t('generic.change') : t('generic.choose')}
                                        </ButtonText>

                                    </div>
                                </CollapsibleContainer>
                            </div>

                            {/* Kontraktskod-väljare — visas under parkeringsplats om det finns koder.
                                Döljs för marketplace-form (koden anges av slutkund vid köp,
                                efter Antons klargörande 2026-04-22 och A5 i Annero-caset). */}
                            {!permit.isMarketplace && contractCodes.length > 0 && (
                                <div className="flex flex-col mb-3 gap-1">
                                    <span className="text-base font-medium pb-1">{t('createPermitPage.contractCode')}</span>
                                    <select
                                        className="flex grow rounded-xl p-2 pl-4 h-11 bg-airpark-gray-300 focus:outline-none"
                                        value={selectedContractCodeId}
                                        onChange={(e) => setSelectedContractCodeId(e.target.value)}
                                    >
                                        <option value="">{t('createPermitPage.noContractCode')}</option>
                                        {contractCodes.map(code => (
                                            <option
                                                key={code.contractCodeId}
                                                value={code.contractCodeId}
                                                disabled={code.isUsed}
                                            >
                                                {code.code}{code.isUsed ? ` (${t('contractCodesPage.used')})` : ''}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <div className="flex flex-col mb-3">
                                <ToggleText
                                    onClick={() => { handleToggleSpecificDates(); }}
                                    isToggled={permit.isRecurring}
                                    title={t('createPermitPage.period')}
                                    defaultText={t('createPermitPage.betweenDates')}
                                    toggledText={t('createPermitPage.specificDays')}
                                    tooltip={t('createPermitPage.tooltipPeriod')} />

                                {!permit.isRecurring &&
                                    <div className="flex flex-col gap-3 mt-3">
                                        <div className="flex flex-col">
                                            <InputDateAndTime title={t('createPermitPage.from')} value={permit.startDateTime} onChange={(date) => { setPermit({ ...permit, startDateTime: date }) }} />
                                        </div>
                                        <ButtonSlider title={t('createPermitPage.noEndDate')} isToggled={permit.noEndDate} onClick={() => { handleToggleNoEndDate(); }}></ButtonSlider>

                                        <CollapsibleContainer isExpanded={!permit.noEndDate}>
                                            <div className="flex flex-col">
                                                <InputDateAndTime title={t('createPermitPage.to')} value={permit.endDateTime ?? new Date()} onChange={(date) => { setPermit({ ...permit, endDateTime: date }) }} />
                                            </div>
                                        </CollapsibleContainer>
                                    </div>
                                }

                                {permit.isRecurring &&
                                    <div className="flex flex-col gap-3 mt-3 mb-3">
                                        <div className="flex flex-col">
                                            <InputDate title={t('createPermitPage.from')} value={permit.startDateTime} onChange={(value) => { setPermit({ ...permit, startDateTime: value }) }} className="w-full" />
                                        </div>
                                        <div className="flex flex-col">
                                            <span className="font-medium pb-1">{t('createPermitPage.onWeekdays')}</span>
                                            <div className="flex justify-between">
                                                <ButtonWeekday title={t('dayShortNames.monday')} isSelected={permit.monday} onClick={() => { setPermit({ ...permit, monday: !permit.monday }) }} />
                                                <ButtonWeekday title={t('dayShortNames.tuesday')} isSelected={permit.tuesday} onClick={() => { setPermit({ ...permit, tuesday: !permit.tuesday }) }} />
                                                <ButtonWeekday title={t('dayShortNames.wednesday')} isSelected={permit.wednesday} onClick={() => { setPermit({ ...permit, wednesday: !permit.wednesday }) }} />
                                                <ButtonWeekday title={t('dayShortNames.thursday')} isSelected={permit.thursday} onClick={() => { setPermit({ ...permit, thursday: !permit.thursday }) }} />
                                                <ButtonWeekday title={t('dayShortNames.friday')} isSelected={permit.friday} onClick={() => { setPermit({ ...permit, friday: !permit.friday }) }} />
                                                <ButtonWeekday title={t('dayShortNames.saturday')} isSelected={permit.saturday} onClick={() => { setPermit({ ...permit, saturday: !permit.saturday }) }} />
                                                <ButtonWeekday title={t('dayShortNames.sunday')} isSelected={permit.sunday} onClick={() => { setPermit({ ...permit, sunday: !permit.sunday }) }} />
                                            </div>
                                        </div>
                                        <div className="flex flex-col">
                                            <span className="font-medium pb-1">{t('createPermitPage.between')}</span>
                                            <div className="flex md:flex-row flex-col">
                                                <InputTime value={permit.timeFrom} className="w-full md:mr-1" onChange={(value) => { setPermit({ ...permit, timeFrom: value + ':00' }) }} />
                                                <InputTime value={permit.timeTo} className="w-full md:ml-1" onChange={(value) => { setPermit({ ...permit, timeTo: value + ':00' }) }} />
                                            </div>
                                        </div>
                                        <CollapsibleContainer isExpanded={!permit.noEndDate}>
                                            <div className="flex flex-col">
                                                <InputDate title={t('createPermitPage.to')} value={permit.endDateTime ?? new Date()} onChange={(value) => { setPermit({ ...permit, endDateTime: value }) }} className="w-full" />
                                            </div>
                                        </CollapsibleContainer>
                                        <ButtonSlider title={t('createPermitPage.noEndDate')} isToggled={permit.noEndDate} onClick={() => { handleToggleNoEndDate(); }}></ButtonSlider>
                                    </div>
                                }
                            </div>

                            {isMarketplace &&
                                <>
                                    <hr className="w-full mt-4 mb-6 border-airpark-gray-400" />
                                    <div className="flex flex-col mb-3">
                                        <DropdownSelector
                                            title={t('createPermitPage.marketplaceAvailability')}
                                            options={[
                                                { title: t('createPermitPage.everyone'), value: 'everyone' },
                                                { title: t('createPermitPage.community'), value: 'community' }]}
                                            defaultValue={permit.isCommunity ? 'community' : 'everyone'}
                                            onChange={(value) => { setPermit({ ...permit, isCommunity: value == 'community' }) }}
                                            tooltip={t('createPermitPage.marketplacePermit')} />
                                    </div>
                                    <div className="flex flex-col mb-6">
                                        <ButtonSlider
                                            title={t('createPermitPage.reAddToMarketplace')}
                                            onClick={() => { setPermit({ ...permit, reAddToMarketplace: !permit.reAddToMarketplace }) }}
                                            isToggled={permit.reAddToMarketplace}
                                            tooltip={t('createPermitPage.reAddToMarketplaceTooltip')}
                                        />
                                    </div>
                                    <div className="flex flex-col mb-6">
                                        <ButtonSlider
                                            title={t('createPermitPage.requiresContractCode')}
                                            onClick={() => { setPermit({ ...permit, requiresContractCode: !permit.requiresContractCode }) }}
                                            isToggled={permit.requiresContractCode}
                                            tooltip={t('createPermitPage.requiresContractCodeTooltip')}
                                        />
                                    </div>
                                </>
                            }


                            {!isSublettingFeatureDisabled &&
                                <>                                
                                    <hr className="w-full mb-6 border-airpark-gray-400" />
                                    
                                    <div className="flex flex-col">
                                        <div className={`${permit.isManualActivation ? 'opacity-50' : ''}`}>
                                            <ButtonSlider
                                                title={t('createPermitPage.isSublettingAllowed')}
                                                onClick={() => { 
                                                    if(!permit.isManualActivation) {
                                                        setPermit({ ...permit, isSublettingAllowed: !permit.isSublettingAllowed })
                                                    }
                                                }}
                                                isToggled={permit.isSublettingAllowed}
                                                tooltip={
                                                permit.isManualActivation
                                                    ? undefined
                                                    : (isMarketplace
                                                        ? t('createPermitPage.isSublettingAllowedMarketplaceDescription')
                                                        : t('createPermitPage.isSublettingAllowedDescription'))
                                                } />
                                            </div>

                                        {!isMarketplace &&
                                            <CollapsibleContainer isExpanded={permit.isSublettingAllowed}>
                                                <div className="mt-3">
                                                    <DropdownSelector
                                                        title={t('createPermitPage.canBeSublettedTo')}
                                                        options={[
                                                            { title: t('createPermitPage.everyone'), value: 'everyone' },
                                                            { title: t('createPermitPage.community'), value: 'community' }]}
                                                        defaultValue={permit.isCommunity ? 'community' : 'everyone'}
                                                        onChange={(value) => { setPermit({ ...permit, isCommunity: value == 'community' }) }} />
                                                </div>
                                            </CollapsibleContainer>
                                        }
                                    </div>
                                </>
                            }

                            <hr className="w-full mt-6 mb-6 border-airpark-gray-400" />

                            {!permitExtId &&
                                <>
                                    <div className="flex flex-col">
                                        <ButtonSlider
                                            title={t('createPermitPage.addUserToCommunity')}
                                            onClick={() => {
                                                const toggled = !permit.addUserToCommunities;
                                                setPermit({ ...permit, addUserToCommunities: toggled });
                                                if (!toggled) {
                                                    setSelectedCommunityExtIds([]);
                                                } else if ((selectedCommunityExtIds || []).length === 0) {
                                                    setDisplayCommunitiesModal(true);
                                                }
                                            }}
                                            isToggled={permit.addUserToCommunities}
                                            tooltip={t('createPermitPage.addUserToCommunityTooltip')}
                                        />
                                        {permit.addUserToCommunities && communities?.length === 0 &&
                                            <div className="flex flex-col mt-3">
                                                <Alert
                                                    template="warning"
                                                    title={t('createPermitPage.noCommunitiesFoundTitle')}
                                                    description={t('createPermitPage.noCommunitiesFoundDescription')} />
                                            </div>
                                        }
                                        <CollapsibleContainer isExpanded={permit.addUserToCommunities && communities?.length > 0}>
                                            <div className="flex flex-col mt-3 gap-3">
                                                <Alert
                                                    template='info'
                                                    title={t('createPermitPage.userWillBeAddedToCommunitiesTitle')}
                                                    description={(selectedCommunityExtIds || []).length > 0
                                                        ? t('createPermitPage.userWillBeAddedToCommunitiesDescription', {
                                                            communities: (communities || [])
                                                                .filter(c => (selectedCommunityExtIds || []).includes(c.communityExtId))
                                                                .map(c => c.name)
                                                                .join(', ')
                                                        })
                                                        : t('createPermitPage.pleaseChooseCommunities')
                                                    } />

                                                <ButtonText className="w-full" onClick={() => setDisplayCommunitiesModal(true)} isSecondary>
                                                    {(selectedCommunityExtIds || []).length > 0 ? t('generic.change') : t('generic.choose')}
                                                </ButtonText>
                                            </div>
                                        </CollapsibleContainer>
                                    </div>

                                    <hr className="w-full mt-6 mb-6 border-airpark-gray-400" />
                                </>
                            }
                            
                            <div className="flex flex-col mb-6 gap-3">
                                { <ToggleText
                                    title={t('createPermitPage.invoicedBy')}
                                    defaultText={t('generic.airPark')}
                                    toggledText={t('createPermitPage.externally')}
                                    isToggled={!permit.isInvoicedByAirPark}
                                    onClick={() => { 
                                        if (!permit.isManualActivation) {
                                            const newIsInvoicedByAirPark = !permit.isInvoicedByAirPark;
                                            setPermit({ 
                                                ...permit, 
                                                price: newIsInvoicedByAirPark ? permit.price : null,
                                                isInvoicedByAirPark: newIsInvoicedByAirPark, 
                                                unit: newIsInvoicedByAirPark ? 'month' : permit.unit 
                                            });
                                        }
                                    }}
                                    tooltip={t('createPermitPage.invoicedByAirparkTooltip')}
                                    disabled={permit.isManualActivation} />}
                                {permit.isInvoicedByAirPark &&
                                    <>
                                        <DropdownSelector
                                            title={t('createPermitPage.price')}
                                            options={[
                                                { title: `${organization.currencySymbol}/${t('priceUnit.month')}`, value: 'month' },
                                                { title: `${organization.currencySymbol}/${t('priceUnit.onetime')}`, value: 'onetime' }]}
                                            defaultValue={permit.unit}
                                            onChange={(value) => { setPermit({ ...permit, unit: value }); }} />
                                        <InputNumber
                                            value={permit.price}
                                            unit={permit.unit
                                                ? `${organization.currencySymbol}/${t(`priceUnit.${permit.unit}`)}`
                                                : organization.currencySymbol}
                                            onChange={handlePriceChange}
                                            min={1}
                                            validationMessage={validationMessages.price} />
                                    </>
                                }
                                {!permit.isInvoicedByAirPark &&
                                    <Alert
                                        title={t('createPermitPage.invoicedExternallyTitle')}
                                        description={t('createPermitPage.invoicedExternallyDescription')}
                                        template='info' />
                                }
                            </div>

                            <ButtonText isLoading={isSaving} onClick={() => { handleSave(); }}>{permitExtId ? t('generic.update') : isMarketplace ? t('generic.create') : t('createPermitPage.issue')}</ButtonText>
                        </div>
                        <SelectParkingSpotsModal
                            display={displayParkingSpotsModal}
                            setDisplay={(value) => { setDisplayParkingSpotsModal(value); }}
                            parkingSpots={parkingSpots}
                            selectedParkingSpots={permit.parkingSpots}
                            setSelectedParkingSpots={handleParkingSpotsChange}
                            isSingleSelectionOnly={false} />

                        <CommunitiesModal
                            display={displayCommunitiesModal}
                            setDisplay={setDisplayCommunitiesModal}
                            communities={communities || []}
                            selectedCommunities={selectedCommunityExtIds || []}
                            setSelectedCommunities={(ids) => {
                                setSelectedCommunityExtIds(ids || []);
                            }}
                            isSingleSelectionOnly={false}
                        >
                            <div className="flex justify-center">
                                <hr className="w-80 mb-2 mt-2" />
                            </div>
                        </CommunitiesModal>
                    </>
                }
                {isLoading &&
                    <WidgetLoadingSkeleton />
                }
            </div>
        </>);
};


const ButtonWeekday = ({ title, isSelected, onClick }) => {

    const className = isSelected ?
        'flex h-12 w-12 rounded-lg bg-airpark-green hover:bg-airpark-green-400 items-center justify-center text-white' :
        'flex h-12 w-12 rounded-lg border-[1px] border-airpark-gray-400 items-center justify-center hover:border-airpark-gray-500';

    return (
        <button className={className} onClick={() => { onClick(); }}>
            <span>{title}</span>
        </button>
    );
}

export default PermitPage; 